import React, { useMemo } from 'react';
import { useGPS } from '../../contexts/GPSContext';
import { useGPSStats } from '../../hooks/useGPSStats';
import GPSSenseNotice from './GPSSenseNotice';
import GPSKpiSection from './GPSKpiSection';
import GPSEvolutionCard from './GPSEvolutionCard';
import GPSRequestTypeChart from './GPSRequestTypeChart';
import GPSAgingChart from './GPSAgingChart';
import GPSShopSignChart from './GPSShopSignChart';
import GPSTieringChart from './GPSTieringChart';
import GPSTopGraph from './GPSTopGraph';
import GPSbrandChart from './GPSBrandsChart';
import GPSStatusDistributionChart from './GPSStatusDistributionChart';
import GPSInterventionRatingsChart from './GPSInterventionRatingsChart';
import ErrorBoundary from '../ui/ErrorBoundary';

const RatingsFallback = () => (
  <div className="bg-white dark:bg-[#1e1e1e] p-5 rounded-2xl border border-gray-200 dark:border-gray-900 text-sm text-gray-600 dark:text-gray-400">
    Satisfaction indisponible pour le moment.
  </div>
);

const GPSSummary = ({ currentUser }) => {
  const gps = useGPS();
  const stats = useGPSStats({ excludeDrafts: true });
  const sense = gps.sense || 'all';

  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;
  const showAging = isAdmin && (sense === 'all' || sense === 'ongoing');

  const furnitureStats = useMemo(() => {
    const counts = new Map();
    (gps.summarySensedIssues || []).forEach((issue) => {
      if (!issue.furniture) return;
      counts.set(issue.furniture, (counts.get(issue.furniture) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
  }, [gps.summarySensedIssues]);

  const problemsStats = useMemo(() => {
    const counts = new Map();
    (gps.summarySensedIssues || []).forEach((issue) => {
      const problems = issue?.problems || [];
      if (Array.isArray(problems)) {
        problems.forEach((problem) => {
          if (!problem) return;
          counts.set(problem, (counts.get(problem) || 0) + 1);
        });
      } else if (problems) {
        counts.set(problems, (counts.get(problems) || 0) + 1);
      }
    });
    return Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
  }, [gps.summarySensedIssues]);

  const assetsStats = useMemo(() => {
    const counts = new Map();
    (gps.summarySensedIssues || []).forEach((issue) => {
      (issue.assets || []).forEach((asset) => {
        if (!asset) return;
        counts.set(asset, (counts.get(asset) || 0) + 1);
      });
    });
    return Array.from(counts.entries()).map(([label, value]) => ({ label, value }));
  }, [gps.summarySensedIssues]);

  return (
    <div className="space-y-4">
      <GPSSenseNotice sense={sense} />

      {/* KPI blocks driven by the global "Afficher" sense */}
      <GPSKpiSection sense={sense} stats={stats} />

      {/* Evolution chart + request-type donut */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 flex flex-col">
          <GPSEvolutionCard stats={stats} sense={sense} />
        </div>
        <div className="md:col-span-1 flex flex-col">
          <GPSRequestTypeChart sense={sense} />
        </div>
      </div>

      {/* Aging backlog (admin + encours-only) beside the satisfaction card. The
          backlog is conditional, so the card spans the full row when it is absent
          rather than sitting in a third of the width next to nothing. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {showAging && (
          <div className="md:col-span-2 flex flex-col">
            <GPSAgingChart stats={stats} />
          </div>
        )}
        <div className={`flex flex-col ${showAging ? 'md:col-span-1' : 'md:col-span-3'}`}>
          <ErrorBoundary fallback={<RatingsFallback />}>
            <GPSInterventionRatingsChart sense={sense} />
          </ErrorBoundary>
        </div>
      </div>

      {/* Status distribution (existing) */}
      <div>
        <div className="mb-3">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Statuts des tickets</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GPSStatusDistributionChart
            title="Tickets en cours"
            filterFn={(issue) => issue?.status_category !== 'DONE'}
            groupByFn={(issue) => issue?.status || 'Non spécifié'}
            dateField="created"
            barColor="#8B5CF6"
            emptyMessage="Aucune donnée de tickets en cours."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
          />
          <GPSStatusDistributionChart
            title="Tickets fermés"
            filterFn={(issue) => issue?.status_category === 'DONE'}
            groupByFn={(issue) => issue?.status || 'Non spécifié'}
            dateField="created"
            colors={['#16a866', '#41c98a', '#0e8a6f', '#0c5e44', '#0a4d38', '#22c55e', '#14b8a6']}
            barColor="#16a866"
            accent={{ bg: '#e9f7ef', color: '#16a866' }}
            emptyMessage="Aucune donnée de tickets fermés."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-4 sm:size-[18px] text-gray-600 dark:text-gray-300">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Existing breakdowns (kept below the new sections) */}
      <div className={`grid grid-cols-1 md:grid-cols-${gps.tierings.length > 1 && gps.brands.length > 1 ? '3' : '2'} gap-4`}>
        <GPSShopSignChart />
        {gps.totalTierings > 1 && <GPSTieringChart />}
        {gps.totalBrands > 1 && <GPSbrandChart />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GPSTopGraph title="Mobiliers concernés" data={furnitureStats} height="300px" />
        <GPSTopGraph title="Problèmes" data={problemsStats} height="300px" />
        <GPSTopGraph title="Éléments nécessaires" data={assetsStats} height="300px" />
      </div>
    </div>
  );
};

export default GPSSummary;
